<template>
  <section class="t-proc" aria-labelledby="guia-tramites-process-title">
    <h2 id="guia-tramites-process-title" class="t-proc__title">
      ¿Cómo es el proceso <span class="hl">de cada tramite?</span>
    </h2>

    <div class="t-proc__grid">
      <article class="t-proc__card t-proc__card--incremento">
        <header class="t-proc__header">
          <span class="t-proc__illustration"><img :src="incrementoImg" alt="" aria-hidden="true" /></span>
          <h3>Incremento <br class="br-t br-m" />de Contrato</h3>
        </header>
        <ul class="t-proc__list">
          <li>Acude a la sucursal más cercana o de tu preferencia.</li>
          <li>Presenta tu identificación oficial junto con los contratos originales.</li>
          <li>Realiza el pago de la diferencia de la cuota de inscripción (sujeto a precalificación de ingresos).</li>
        </ul>
      </article>

      <article class="t-proc__card t-proc__card--facturacion">
        <header class="t-proc__header">
          <span class="t-proc__illustration"><img :src="facturacionImg" alt="" aria-hidden="true" /></span>
          <h3>Facturación</h3>
        </header>
        <ul class="t-proc__list">
          <li>Comparte RFC, nombre o razón social, código postal fiscal, régimen fiscal y uso de CFDI.</li>
          <li>Solicita el formato de alta o modificación de datos fiscales por correo, WhatsApp o sucursal.</li>
          <li>Complétalo y adjunta tu CSF, identificación oficial y comprobante de domicilio con tu asesor.</li>
        </ul>
      </article>

      <article id="tramites-pagos" class="t-proc__card t-proc__card--domiciliacion">
        <header class="t-proc__header">
          <span class="t-proc__illustration"><img :src="domiciliacionImg" alt="" aria-hidden="true" /></span>
          <h3>Domiciliación</h3>
        </header>
        <p class="t-proc__step">
          <strong>Paso 1</strong><br />Solicita el formato a través de correo electrónico, WhatsApp, sitio web o
          sucursales.
        </p>
        <p class="t-proc__step">
          <strong>Paso 2</strong><br />Llena el formato y ten a la mano un estado de cuenta bancario (no mayor a 3
          meses) con cuenta CLABE e identificación oficial (si no eres el titular, adjunta la autorización y documentos
          del titular).
        </p>
        <p class="t-proc__step">
          <strong>Paso 3</strong><br />Entrega el formato y documentación en sucursal o envíalo por correo a
          <a class="t-proc__mail" href="mailto:atencionaclientes@hircasa.com.mx">atencionaclientes@hircasa.com.mx.</a>
        </p>
      </article>

      <article class="t-proc__card t-proc__card--aclaraciones">
        <header class="t-proc__header">
          <span class="t-proc__illustration"><img :src="aclaracionesImg" alt="" aria-hidden="true" /></span>
          <h3>Aclaraciones <br class="br-t br-m" />de Pagos</h3>
        </header>
        <p v-for="item in aclaraciones" :key="item.label" class="t-proc__note">
          <strong>{{ item.label }}</strong
          ><br />{{ item.text }}
        </p>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import incrementoImg from '../../../assets/tramites-figma/il-incremento.png'
import facturacionImg from '../../../assets/tramites-figma/il-facturacion.png'
import domiciliacionImg from '../../../assets/tramites-figma/il-domiciliacion.png'
import aclaracionesImg from '../../../assets/tramites-figma/il-aclaraciones.png'

const aclaraciones = [
  {
    label: 'Redistribución de pago:',
    text: 'Tu pago registrado en un contrato será distribuido entre dos o más contratos para su correcta aplicación.'
  },
  {
    label: 'Pago no aplicado:',
    text: 'Tienes comprobante pero el pago aún no se refleja en tu contrato.'
  },
  {
    label: 'Pago a cuenta errónea:',
    text: 'El pago se realizó a una cuenta de HIR que no corresponde a tus cuotas periódicas.'
  },
  {
    label: 'Pago duplicado:',
    text: 'Realizaste un pago adicional sin considerar que tenías activa la domiciliación.'
  }
]
</script>

<style scoped>
.t-proc {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-6);
}

.t-proc__title {
  margin: 0;
  color: var(--ds-purple-900);
  font-family: var(--ds-font-family-bebas);
  font-size: 32px;
  font-weight: 700;
  line-height: normal;
}
.t-proc__title .hl {
  color: #6d59ea;
}

.t-proc__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--ds-space-5);
  align-items: start;
}

.t-proc__card {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-4);
  align-self: stretch;
  padding: var(--ds-space-5) var(--ds-space-4);
  box-sizing: border-box;
  background: var(--ds-grey-50);
  border-radius: 24px;
}
.t-proc__card--aclaraciones {
  align-self: start;
}

.t-proc__header {
  display: flex;
  align-items: center;
  gap: var(--ds-space-4);
}
.t-proc__header h3 {
  margin: 0;
  color: var(--ds-text-body);
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
}

/* Caja protectora de 120px (círculo de 112 con 4 de margen) y la ilustración de 80px */
.t-proc__illustration {
  display: flex;
  flex: 0 0 112px;
  align-items: center;
  justify-content: center;
  width: 112px;
  height: 112px;
  margin: 4px;
  border-radius: 56px;
  background: var(--ds-tertiary);
}
.t-proc__illustration img {
  width: 80px;
  height: 80px;
  object-fit: contain;
}

.t-proc__list {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-4);
  margin: 0;
  padding: 0 0 0 var(--ds-space-5);
  color: var(--ds-text-body);
  font-size: 16px;
  line-height: 24px;
}

.t-proc__step,
.t-proc__note {
  margin: 0;
  color: var(--ds-text-body);
  font-size: 16px;
  line-height: 24px;
}
.t-proc__step strong {
  color: var(--ds-purple-900);
  font-weight: 600;
}
.t-proc__note strong {
  font-weight: 600;
}
.t-proc__mail {
  color: var(--ds-purple-900);
  text-decoration: none;
}

@media (max-width: 1024px) {
  .t-proc__grid {
    column-gap: var(--ds-space-4);
    row-gap: var(--ds-space-5);
  }
}

@media (max-width: 599px) {
  .t-proc__grid {
    grid-template-columns: 1fr;
    gap: var(--ds-space-6);
  }
  .t-proc__card {
    padding: var(--ds-space-4);
  }
}
</style>
